import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { AuthenticatedTemplate, useIsAuthenticated, useMsal, useMsalAuthentication } from "@azure/msal-react";
import { InteractionStatus, InteractionType } from "@azure/msal-browser";
import { Button, message, Select, Spin, Switch } from "antd";
import axios, { AxiosError } from "axios";

import UserContext, { UserType } from "../../themeContext/UserContext";
import GroupDocumentContext from "../../themeContext/GroupDocumentContext";
import MachineDocumentContext from "../../themeContext/MachineDocumentContext";
import SelectedGroupContext from "../../themeContext/selectedGroupContext";
import { downloadReportClicked, getGroupAndMachineDocuments } from "../../Common/commonFunctions";
import { getUserDetails } from "../../axios/portalData";
import { GroupDocument, MachineDocument } from "../../Types/dashboardTypes";

import { accountType } from "../../Common/types";
import EacLogo from "../../Icons/EacLogo";
import Sun from "../../Icons/Sun";
import Moon from "../../Icons/Moon";
import "./DashboardHeader.less";
import GroupsMachinesReFetchFlagContext from "../../themeContext/GroupsMachinesReFetchFlagContext";

type DashboardHeaderOptions = {
  display: string;
  path: string;
  accounts: "all" | Array<accountType>;
};

const DashboardHeader: React.FC = () => {
  const { login, error } = useMsalAuthentication(InteractionType.Redirect);
  const [userInfo, setUserInfo] = useState<UserType>({
    firstName: "",
    accountType: "free" as accountType,
    companyName: "",
    colorTheme: "",
  });
  const [groupDocument, setGroupDocument] = useState<GroupDocument>({
    groupDocument: {
      b2c_oid: "",
      documentType: "",
      groups: { None: [] },
      id: "",
    },
    groupNames: [],
  });
  const [machineDocuments, setMachineDocuments] = useState<MachineDocument[] | undefined>(undefined);
  const [selectedGroup, setSelectedGroup] = useState<string | null>("None");
  const [groupsMachinesReFetchFlag, setGroupsMachinesReFetchFlag] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  if (error) {
    login(InteractionType.Redirect);
  }

  const navigate = useNavigate();
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // If the path is empty, the app  will logout for that option
  const options: DashboardHeaderOptions[] = [
    { display: "Home", path: "/dashboard", accounts: "all" },
    { display: "Account", path: "/dashboard/account", accounts: "all" },
    { display: "Subscription", path: "/dashboard/subscription", accounts: "all" },
    { display: "Machines", path: "/dashboard/machines", accounts: "all" },
    { display: "Groups", path: "/dashboard/groups", accounts: ["standard", "premium"] },
    { display: "Logout", path: "", accounts: "all" },
  ];

  // this needs to be done on the token eventually, for now, it is local storage....
  const changeColorTheme = () => {
    if (userInfo.colorTheme == "light") {
      document.body.classList.contains("light") && document.body.classList.remove("light");
      document.body.classList.add("dark");
      setUserInfo((userInfo) => {
        return { ...userInfo, colorTheme: "dark" };
      });
      localStorage.setItem("colorTheme", "dark");
    } else {
      document.body.classList.contains("dark") && document.body.classList.remove("dark");
      document.body.classList.add("light");
      setUserInfo((userInfo) => {
        return { ...userInfo, colorTheme: "light" };
      });
      localStorage.setItem("colorTheme", "light");
    }
  };

  // this is used in getting user information from the token below
  const settingAccount = (account?: {
    name: string;
    idTokenClaims?: {
      extension_AccountType?: "free" | "standard" | "premium";
      extension_CompanyName?: string;
    };
  }) => {
    setIsLoading(true);
    const theme = localStorage.getItem("colorTheme");
    document.body.classList.remove("light");
    document.body.classList.remove("dark");
    theme && document.body.classList.add(theme);

    const userCompanyName =
      account?.idTokenClaims?.extension_CompanyName != undefined ? account?.idTokenClaims?.extension_CompanyName : "";

    // getting everything from existing token except the name because this might change in accounts page
    getUserDetails()
      .then((res) => {
        setUserInfo({
          firstName: res.name,
          accountType: res.accountType,
          companyName: userCompanyName,
          colorTheme: theme ? theme : "light",
        });
        setIsLoading(false);
      })
      .catch((error: Error | AxiosError) => {
        setIsLoading(false);
        if (axios.isAxiosError(error)) {
          console.log("AxiosError message: ", error);
          message.error(error.message);
        } else {
          console.log("unexpected error: ", error);
          message.error("An unexpected error occurred");
        }
      });
  };

  // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/FAQ.md#how-do-i-handle-the-redirect-flow-in-a-react-app
  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const account: any = instance.getActiveAccount();
      settingAccount(account);
    }
  }, [inProgress, isAuthenticated, accounts, instance]);

  // this is seperated from above because when the flag changes we don't want to set the account every time
  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None && groupsMachinesReFetchFlag) {
      getGroupAndMachineDocuments(setGroupDocument, setMachineDocuments, setGroupsMachinesReFetchFlag);
    }
  }, [isAuthenticated, inProgress, groupsMachinesReFetchFlag]);

  const { Option } = Select;

  const RenderOptions = (options: DashboardHeaderOptions[]) => {
    const groupOptions: JSX.Element[] = [];
    options.map((option) => {
      (option.accounts == "all" || option.accounts.includes(userInfo.accountType)) &&
        groupOptions.push(
          <Option key={option.display} value={option.path}>
            {option.display}
          </Option>
        );
    });
    return groupOptions;
  };

  const handleChange = (e: { value: string }) => {
    const path = e.value;
    if (path !== "") {
      navigate(path);
    } else {
      instance.logout();
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }
  return (
    <>
      <AuthenticatedTemplate>
        <UserContext.Provider value={{ userInfo, setUserInfo }}>
          <GroupDocumentContext.Provider value={{ groupDocument, setGroupDocument }}>
            <MachineDocumentContext.Provider value={{ machineDocuments, setMachineDocuments }}>
              <SelectedGroupContext.Provider value={{ selectedGroup, setSelectedGroup }}>
                <GroupsMachinesReFetchFlagContext.Provider
                  value={{ groupsMachinesReFetchFlag, setGroupsMachinesReFetchFlag }}
                >
                  <div className="flex space-between dashboard-header-margins">
                    <button
                      className="invisible-button"
                      onClick={() => {
                        navigate("/dashboard");
                      }}
                    >
                      <EacLogo />
                      <span className="dashboard-header-text">Onramp</span>
                    </button>
                    <div className="flex dashboard-header-dropdown" style={{ columnGap: "40px" }}>
                      {userInfo.accountType === "free" ? (
                        window.location.pathname !== "/dashboard/new-machine" && (
                          <Button
                            className="primary-button"
                            onClick={() => {
                              navigate("/dashboard/new-machine");
                            }}
                          >
                            New Machine
                          </Button>
                        )
                      ) : (
                        <Button
                          className="secondary-button"
                          htmlType="submit"
                          onClick={() => downloadReportClicked(selectedGroup, machineDocuments, groupDocument)}
                        >
                          Download report
                        </Button>
                      )}
                      <Select
                        loading={userInfo.firstName == ""}
                        showArrow={true}
                        labelInValue
                        value={userInfo.firstName == "" ? null : { value: `Welcome, ${userInfo.firstName}` }}
                        onChange={handleChange}
                        listHeight={400}
                        getPopupContainer={(triggerNode) => triggerNode.parentElement}
                        dropdownRender={(menu) => (
                          <>
                            {menu}
                            <hr className="dropdown-line" />
                            <div className="theme-text-switch">
                              <div>Theme</div>
                              <Switch
                                onClick={changeColorTheme}
                                checkedChildren={<Sun />}
                                unCheckedChildren={<Moon />}
                                defaultChecked={userInfo.colorTheme == "light" ? true : false}
                              />
                            </div>
                          </>
                        )}
                      >
                        {RenderOptions(options)}
                      </Select>
                    </div>
                  </div>
                  <Button
                    type="link"
                    className="config-download"
                    onClick={() => window.open("https://eacpds.app.box.com/notes/1139355147498", "_blank")}
                  >
                    <div className="need-help-link">Have a suggestion or feedback, Click here!!</div>
                  </Button>
                  {/* <div className="flex plans">
                    <button
                      onClick={() =>
                        setUserInfo((userInfo) => {
                          return { ...userInfo, accountType: "free" };
                        })
                      }
                    >
                      free
                    </button>
                    <button
                      onClick={() =>
                        setUserInfo((userInfo) => {
                          return { ...userInfo, accountType: "standard" };
                        })
                      }
                    >
                      standard
                    </button>
                    <button
                      onClick={() =>
                        setUserInfo((userInfo) => {
                          return { ...userInfo, accountType: "premium" };
                        })
                      }
                    >
                      premium
                    </button>
                  </div> */}

                  <Outlet />
                </GroupsMachinesReFetchFlagContext.Provider>
              </SelectedGroupContext.Provider>
            </MachineDocumentContext.Provider>
          </GroupDocumentContext.Provider>
        </UserContext.Provider>
      </AuthenticatedTemplate>
    </>
  );
};

export default DashboardHeader;
